import { isEmpty, isUndefined, without } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import type { EmptyOption, EmptyOptionValue, Option, OptionValue } from './option';

export const useValue = (
  value: EmptyOptionValue | OptionValue | undefined | null,
  onValueChange: (value: EmptyOptionValue | OptionValue) => void,
  allowDeselect: boolean,
) => {
  const [selectedValue, setSelectedValue] = useState(value ?? undefined);

  useEffect(() => setSelectedValue(value ?? undefined), [value]);

  const selectValue = useCallback((value: EmptyOptionValue | OptionValue) => {
    if (isUndefined(value) && !allowDeselect) {
      return;
    }

    setSelectedValue(value);
    onValueChange(value);
  }, [allowDeselect, onValueChange]);

  const isSelectedOption = useCallback(({ value }: EmptyOption | Option) => selectedValue === value, [selectedValue]);

  return {
    selectedValue,
    selectValue,
    isSelectedOption,
  };
};

export function useValues(
  values: OptionValue[],
  onValueChange: (values: OptionValue[]) => void,
) {
  const [selectedValues, setSelectedValues] = useState(values);

  useEffect(() => setSelectedValues(values), [values]);

  const updateSelectedValues = useCallback((values: OptionValue[]) => {
    setSelectedValues(values);
    onValueChange(values);
  }, [onValueChange]);

  const isSelectedValue = useCallback((value: OptionValue) => selectedValues.includes(value), [selectedValues]);
  const selectValue = useCallback((value: OptionValue) => updateSelectedValues([...values, value]), [updateSelectedValues, values]);
  const deselectValue = useCallback((value: OptionValue) => updateSelectedValues(without(values, value)), [updateSelectedValues, values]);
  const toggleValue = useCallback((value: OptionValue) => isSelectedValue(value) ? deselectValue(value) : selectValue(value), [deselectValue, isSelectedValue, selectValue]);

  return {
    isSelectedValue,
    selectValue,
    deselectValue,
    toggleValue,
    selectedValues,
    hasSelectedValues: !isEmpty(selectedValues),
  };
}
